import { makeStyles, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import InfoIcon from '@material-ui/icons/Info';
import { memo, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useRouter } from 'next/router';
import * as seatStyles from './Seat.module.css';
import FareConfirmModal from '../../FareConfirmModal';
import { cabinClassEnum } from '../../../../../../../constants';
import { updateTripRequest } from '../../../../../../../store/slices/trip';
import computeTotalSeatCost from '../../../../../../../helpers/computeTotalSeatCost';
import getCurrencySymbol from '../../../../../../../helpers/currencySymbol';

const tooltipStyles = makeStyles((theme) => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: '#FFF',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
    boxShadow: theme.shadows[1],
    padding: 10,
    marginTop: 5,
  },
  arrow: {
    '&:before': {
      border: '1px solid #f5f5f9',
    },
    color: theme.palette.common.white,
  },
}));

const exitRowStyles = { left: 50, fontSize: '3.5rem' };
const seatButtonStyles = { fontSize: 10, width: 42, height: 42 };

const Seat = ({
  seat, flight, doAddExitRowIndicator, canPickSeat, onSeatSelect,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { tripId } = router.query;
  const currencyCode = flight && flight.selectedFare && flight.selectedFare.currencyCode;
  const { passengers } = useSelector((state) => state.trip);
  const isSeatSelected = passengers !== null && _.find(passengers,
    (p) => p.travelerPreferences && _.find(p.travelerPreferences.flightSeatNumbers || {},
      (sn, key) => sn && key === flight.flightNumber && sn.seatNumber === seat.seatNumber) != null);
  const currencySymbol = getCurrencySymbol(currencyCode);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const classes = tooltipStyles();
  const formatSeatPrice = (decimalScale, currency = '') => {
    const relativeSeatPrice = seat.price;
    if (relativeSeatPrice !== null && relativeSeatPrice !== 0) {
      return (
        <NumberFormat
          value={relativeSeatPrice}
          prefix={(relativeSeatPrice > 0 ? '+' : '') + currency}
          suffix={' '}
          displayType="text"
          thousandSeparator
          allowNegative
          decimalScale={decimalScale}
        />
      );
    }
    return false;
  };

  const getTooltipTitle = () => (
    <>
      <b className="text-theme">
        {seat.seatNumber}
        {' '}
        {seat.columnCharacteristic && `(${seat.columnCharacteristic})`}
        <br />
        {!seat.isBlocked && !seat.isOccupied
          && formatSeatPrice(2) ? (
            <>
              Price:
              {' '}
              {formatSeatPrice(2, currencySymbol)}
            </>
          ) : ''}
      </b>
      <div>
        {seat.commercialName}
      </div>
      <div>
        {seat.cabin}
      </div>
      {seat.facilities && seat.facilities.length > 0 && (
        <>
          <div className="font-bold mt-2">
            Facilities:
          </div>
          <ul style={{ listStyleType: 'circle' }}>
            {seat.facilities.map((f) => <li className="ml-5" key={f}>{f}</li>)}
          </ul>
        </>
      )}

      {seat.limitations && seat.limitations.length > 0 && (
        <>
          <div className="font-bold mt-2">
            Limitations:
          </div>
          <ul style={{ listStyleType: 'circle' }}>
            {seat.limitations.map((f) => <li className="ml-5" key={f}>{f}</li>)}
          </ul>
        </>
      )}

      {seat.occupation && seat.occupation.length > 0 && (
        <>
          <div className="font-bold mt-2">
            Occupation:
          </div>
          <ul style={{ listStyleType: 'circle' }}>
            {seat.occupation.map((f) => <li className="ml-5" key={f}>{f}</li>)}
          </ul>
        </>
      )}

      {((seat.hasDifferentFareClass && !seat.isBlocked && !seat.isOccupied)
      || seat.isDisabled) && (
        <div className="text-red-600 text-center mt-2 flex items-center gap-1">
          <div className="inline-block align-top"><InfoIcon /></div>
          <div className="inline-block w-4/5 text-left">{seat.isDisabled ? 'Paid seats are disabled' : 'This seat is from different fare class than the selected fare class.'}</div>
        </div>
      )}
    </>
  );

  const onClick = () => {
    if (!canPickSeat || seat.isDisabled) return;
    if (seat.hasDifferentFareClass) {
      setIsModalOpen(true);
    } else if (seat.isSeatAvailable && (seat.price || 0) >= 0) {
      onSeatSelect({
        ...seat,
        isAlreadySelected: isSeatSelected,
      });
    }
  };

  const handleCloseModal = ({ fare }) => {
    setIsModalOpen(false);
    if (fare) {
      dispatch(updateTripRequest({
        tripId,
        selectedFlightFareIndex: _.findIndex(flight.allFares, (f) => f === fare),
      }));
      onSeatSelect({
        ...seat,
        doSelectSeatAfterTripUpdate: true,
      });
    }
  };

  const getMatchingFares = () => {
    const matchingFare = flight.allFares.find((f) => f.fareClass === seat.fareClass);
    return _.orderBy(flight.allFares
      .filter((f) => f.originalCabinClass === (matchingFare || {}).originalCabinClass
        && f.cabinClass !== cabinClassEnum.BASIC_ECONOMY), 'price');
  };

  const exitRowIndicator = (doAddExitRowIndicator && <span className="absolute font-bold top-0 leading-none text-red-500" style={exitRowStyles}> &gt; </span>);

  return (seat.noSeatAtThisLocation ? <td width={50} className="relative">{exitRowIndicator}</td> : (
    <>
      <td height={50} className={`p-1 relative ${seat.isSeatAvailable ? 'cursor-pointer' : ''}`}>
        <Tooltip classes={classes} title={getTooltipTitle(seat)} placement="top">
          <button type="button" className={`border text-center py-2 rounded-md leading-5 outline-none ${seatStyles[seat.seatType]} ${isSeatSelected && seatStyles['selected-seat']}`} style={seatButtonStyles} onClick={onClick}>
            {!seat.isBlocked && !seat.isOccupied && formatSeatPrice(0, currencySymbol && currencySymbol.length === 1 ? currencySymbol : '')}
          </button>
        </Tooltip>
        {exitRowIndicator}
      </td>
      {isModalOpen && (
        <FareConfirmModal
          onCancel={handleCloseModal}
          onConfirm={handleCloseModal}
          selectedFare={flight.selectedFare}
          matchingFares={getMatchingFares()}
          seatPrice={seat.seatPrice}
          totalSeatCost={computeTotalSeatCost(Object.values(passengers || {}))}
        />
      )}
    </>
  ));
};

Seat.propTypes = {
  canPickSeat: PropTypes.bool.isRequired,
  flight: PropTypes.shape({
    flightNumber: PropTypes.string,
    fareClass: PropTypes.string,
    allFares: PropTypes.arrayOf(PropTypes.shape({})),
    selectedFare: PropTypes.shape({
      currencyCode: PropTypes.string,
    }),
  }).isRequired,
  seat: PropTypes.shape({
    seatNumber: PropTypes.string,
    price: PropTypes.number,
    seatType: PropTypes.string,
    cabin: PropTypes.string,
    commercialName: PropTypes.string,
    columnCharacteristic: PropTypes.string,
    facilities: PropTypes.arrayOf(PropTypes.string),
    limitations: PropTypes.arrayOf(PropTypes.string),
    occupation: PropTypes.arrayOf(PropTypes.string),
    isSeatAvailable: PropTypes.bool,
    isDisabled: PropTypes.bool,
    noSeatAtThisLocation: PropTypes.bool,
    hasDifferentFareClass: PropTypes.bool,
    isBlocked: PropTypes.bool,
    isOccupied: PropTypes.bool,
    fareClass: PropTypes.string,
    seatPrice: PropTypes.number,
  }).isRequired,
  doAddExitRowIndicator: PropTypes.bool,
  onSeatSelect: PropTypes.func.isRequired,
};

Seat.defaultProps = {
  doAddExitRowIndicator: false,
};

export default memo(Seat);
